import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { invoicesAPI, casesAPI, organizationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Receipt, Plus, Trash2, Send, Loader2, Clock, CheckCircle, AlertCircle, Search, Building2, FileText, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

export default function InvoicesPage() {
  const { isSuperAdmin, isAdmin } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [cases, setCases] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [invoiceType, setInvoiceType] = useState('client');
  const [caseId, setCaseId] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [desc, setDesc] = useState('');
  const [price, setPrice] = useState('');
  const [payingInvoice, setPayingInvoice] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const promises = [invoicesAPI.getAll(), casesAPI.getAll()];
        if (isSuperAdmin) {
          promises.push(organizationsAPI.getAll());
        }
        const results = await Promise.all(promises);
        setInvoices(results[0].data.invoices || []);
        setCases(results[1].data || []);
        if (isSuperAdmin && results[2]) {
          setOrganizations(results[2].data || []);
        }
      } catch (e) { toast.error('Failed to load data'); }
      setLoading(false);
    };
    load();
  }, [isSuperAdmin]);

  const resetForm = () => {
    setInvoiceType('client');
    setCaseId('');
    setOrganizationId('');
    setRecipientEmail('');
    setDueDate('');
    setDesc('');
    setPrice('');
  };

  const create = async (e) => {
    e.preventDefault();
    if (!dueDate || !desc || !price) { toast.error('Fill all fields'); return; }
    
    if (invoiceType === 'client' && !caseId) {
      toast.error('Please select a case');
      return;
    }
    if (invoiceType === 'organization' && !organizationId) {
      toast.error('Please select an organization');
      return;
    }

    try {
      const payload = {
        invoice_type: invoiceType,
        due_date: dueDate,
        tax_rate: 0,
        notes: '',
        line_items: [{ description: desc, quantity: 1, unit_price: parseFloat(price), amount: parseFloat(price) }]
      };

      if (invoiceType === 'client') {
        payload.case_id = caseId;
      } else {
        payload.organization_id = organizationId;
        if (recipientEmail) payload.recipient_email = recipientEmail;
      }

      await invoicesAPI.create(payload);
      toast.success('Invoice created');
      setIsOpen(false);
      resetForm();
      const inv = await invoicesAPI.getAll();
      setInvoices(inv.data.invoices || []);
    } catch (e) { 
      toast.error(e.response?.data?.detail || 'Failed to create invoice'); 
    }
  };

  const send = async (id) => { try { await invoicesAPI.sendEmail(id); toast.success('Sent'); } catch (e) { toast.error('Failed'); } };
  const del = async (id) => { if (!confirm('Delete?')) return; try { await invoicesAPI.delete(id); toast.success('Deleted'); setInvoices(invoices.filter(i => i.id !== id)); } catch (e) { toast.error('Failed'); } };
  
  const pay = async (invoice) => {
    setPayingInvoice(invoice.id);
    try {
      const response = await invoicesAPI.createCheckout(invoice.id, window.location.origin);
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment failed');
    } finally {
      setPayingInvoice(null);
    }
  };

  const badge = (s) => <Badge className={s === 'paid' ? 'bg-green-100 text-green-800' : s === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>{s}</Badge>;

  const list = invoices.filter(i => (filter === 'all' || i.status === filter) && (i.invoice_number?.includes(search) || i.case_name?.toLowerCase().includes(search.toLowerCase()) || i.organization_name?.toLowerCase().includes(search.toLowerCase())));
  const pending = invoices.filter(i => i.status === 'pending').length;
  const paid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);
  const overdue = invoices.filter(i => i.status === 'overdue').length;

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        <div className="flex items-center justify-between mb-8">
          <div><h1 className="text-2xl font-bold flex items-center gap-3"><Receipt className="w-7 h-7 text-primary" />Invoices</h1><p className="text-muted-foreground">Manage invoices</p></div>
          <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild><Button className="bg-primary text-white" data-testid="create-invoice-btn"><Plus className="w-4 h-4 mr-2" />Create</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                {isSuperAdmin && (
                  <div>
                    <Label>Invoice Type</Label>
                    <Select value={invoiceType} onValueChange={(v) => { setInvoiceType(v); setCaseId(''); setOrganizationId(''); }}>
                      <SelectTrigger data-testid="invoice-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client">Client Invoice</SelectItem>
                        <SelectItem value="organization">Organization Invoice</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {invoiceType === 'client' && (
                  <div>
                    <Label>Case</Label>
                    <Select value={caseId} onValueChange={setCaseId}>
                      <SelectTrigger data-testid="case-select"><SelectValue placeholder="Select case" /></SelectTrigger>
                      <SelectContent>{cases.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {invoiceType === 'organization' && isSuperAdmin && (
                  <>
                    <div>
                      <Label>Organization</Label>
                      <Select value={organizationId} onValueChange={setOrganizationId}>
                        <SelectTrigger data-testid="organization-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
                        <SelectContent>{organizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Recipient Email (optional)</Label>
                      <Input type="email" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="Override default email" data-testid="recipient-email-input" />
                    </div>
                  </>
                )}
                <div><Label>Due Date</Label><Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="due-date-input" /></div>
                <div><Label>Description</Label><Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Service description" data-testid="description-input" /></div>
                <div><Label>Amount ($)</Label><Input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="0.00" data-testid="amount-input" /></div>
                <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button><Button type="submit" className="bg-blue-600 text-white" data-testid="submit-invoice-btn">Create</Button></div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-4 gap-6 mb-8">
          <Card><CardContent className="pt-6 flex items-center gap-4"><div className="p-3 bg-blue-100 rounded-lg"><Receipt className="w-6 h-6 text-blue-600" /></div><div><p className="text-2xl font-bold">{invoices.length}</p><p className="text-sm text-gray-500">Total</p></div></CardContent></Card>
          <Card><CardContent className="pt-6 flex items-center gap-4"><div className="p-3 bg-yellow-100 rounded-lg"><Clock className="w-6 h-6 text-yellow-600" /></div><div><p className="text-2xl font-bold">{pending}</p><p className="text-sm text-gray-500">Pending</p></div></CardContent></Card>
          <Card><CardContent className="pt-6 flex items-center gap-4"><div className="p-3 bg-green-100 rounded-lg"><CheckCircle className="w-6 h-6 text-green-600" /></div><div><p className="text-2xl font-bold">${paid.toFixed(0)}</p><p className="text-sm text-gray-500">Paid</p></div></CardContent></Card>
          <Card><CardContent className="pt-6 flex items-center gap-4"><div className="p-3 bg-red-100 rounded-lg"><AlertCircle className="w-6 h-6 text-red-600" /></div><div><p className="text-2xl font-bold">{overdue}</p><p className="text-sm text-gray-500">Overdue</p></div></CardContent></Card>
        </div>

        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" /></div>
          <Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-40"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="overdue">Overdue</SelectItem></SelectContent></Select>
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-gray-50"><tr><th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Invoice #</th><th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Type</th><th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Recipient</th><th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Amount</th><th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Due</th><th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Status</th><th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th></tr></thead>
              <tbody className="divide-y">
                {list.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">No invoices</td></tr>}
                {list.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-6 py-4 font-mono font-medium">{inv.invoice_number}</td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" className={inv.invoice_type === 'organization' ? 'border-purple-300 text-purple-700' : 'border-blue-300 text-blue-700'}>
                        {inv.invoice_type === 'organization' ? <Building2 className="w-3 h-3 mr-1 inline" /> : <FileText className="w-3 h-3 mr-1 inline" />}
                        {inv.invoice_type === 'organization' ? 'Org' : 'Client'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium">{inv.invoice_type === 'organization' ? inv.organization_name : inv.case_name}</p>
                      <p className="text-sm text-gray-500">{inv.recipient_email || inv.client_email || '-'}</p>
                    </td>
                    <td className="px-6 py-4 font-semibold">${inv.total?.toFixed(2)}</td>
                    <td className="px-6 py-4">{new Date(inv.due_date).toLocaleDateString()}</td>
                    <td className="px-6 py-4">{badge(inv.status)}</td>
                    <td className="px-6 py-4 text-right space-x-1">
                      {inv.status !== 'paid' && inv.invoice_type === 'organization' && !isSuperAdmin && (
                        <Button 
                          variant="default" 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => pay(inv)} 
                          disabled={payingInvoice === inv.id}
                          data-testid={`pay-invoice-${inv.id}`}
                        >
                          {payingInvoice === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                          Pay Now
                        </Button>
                      )}
                      {inv.status !== 'paid' && (inv.recipient_email || inv.client_email) && <Button variant="ghost" size="sm" onClick={() => send(inv.id)} data-testid={`send-invoice-${inv.id}`}><Send className="w-4 h-4" /></Button>}
                      {inv.status !== 'paid' && <Button variant="ghost" size="sm" onClick={() => del(inv.id)} data-testid={`delete-invoice-${inv.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
